/**
 * GameResultModal helper.
 * Pure logic for avatar image sources.
 * Winner shows happy → idle; loser shows sad → angry → idle.
 */

import { DEFAULT_AVATAR_ID, type AvatarId } from '../../../constants/avatarIds';
import { getAvatarLoadAttempts } from '../../../src/avatars/helpers/getAvatarAsset';
import { normalizeDisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import type { AvatarMood, DisplayAvatarId } from '../../../src/avatars/types/avatar.types';

/** User-pickable avatars plus reserved `monkey` mascot (API / guest flows). */
export type GameResultModalAvatarId = AvatarId | 'monkey';

function sourcesToUris(sources: ReturnType<typeof getAvatarLoadAttempts>): string[] {
  const uris: string[] = [];
  for (const src of sources) {
    if (src && typeof src === 'object' && 'uri' in src && typeof (src as { uri: unknown }).uri === 'string') {
      uris.push((src as { uri: string }).uri);
    }
  }
  return uris;
}

/**
 * Resolve ordered CDN avatar URIs for winner (happy → idle) or loser (sad → angry → idle).
 * expo-image tries each string source in order, giving automatic CDN fallback.
 */
export function getAvatarSourcesForOutcome(
  avatarId: GameResultModalAvatarId,
  isWinner: boolean
): string[] {
  const resolvedAvatarId = normalizeDisplayAvatarId(avatarId) as DisplayAvatarId;
  const mood: AvatarMood = isWinner ? 'happy' : 'sad';
  const attempts = getAvatarLoadAttempts(resolvedAvatarId, mood, undefined);
  const uris = sourcesToUris(attempts);
  if (uris.length > 0) return uris;
  return sourcesToUris(getAvatarLoadAttempts(DEFAULT_AVATAR_ID as DisplayAvatarId, mood, undefined));
}

/** Returns the primary CDN URL for the given avatar + outcome (first in the fallback chain). */
export function getAvatarUrlForOutcome(
  avatarId: GameResultModalAvatarId,
  isWinner: boolean
): string {
  return getAvatarSourcesForOutcome(avatarId, isWinner)[0] ?? '';
}
